import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  ViewColumn as ViewColumnIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { dataService } from '../services/dataService';
import type { SupplierMaster, CustomColumn } from '../types';

interface ColumnFilters {
  razonSocial: string;
  articulo: string;
  ordenes: string;
  montoTotalUSD: string;
  nombreRepresentante: string;
  apellidoRepresentante: string;
  telefonoRepresentante: string;
  emailRepresentante: string;
  cuentasContables: string;
  clases: string;
}

const SupplyMaster: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierMaster[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<SupplierMaster[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Selección múltiple
  const [selected, setSelected] = useState<Set<string>>(new Set());
  
  // Filtros por columna
  const [filters, setFilters] = useState<ColumnFilters>({
    razonSocial: '',
    articulo: '',
    ordenes: '',
    montoTotalUSD: '',
    nombreRepresentante: '',
    apellidoRepresentante: '',
    telefonoRepresentante: '',
    emailRepresentante: '',
    cuentasContables: '',
    clases: '',
  });

  // Columnas customizables (hasta 5)
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customColumnsMenuAnchor, setCustomColumnsMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Dialog de edición
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState<SupplierMaster | null>(null);

  useEffect(() => {
    loadData();
    loadCustomColumns();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, suppliers]);

  const loadData = async () => {
    try {
      setLoading(true);
      await dataService.loadPurchaseOrders('/PurchaseOrders.csv');
      
      const supplierData = dataService.getSupplierMasterData();
      setSuppliers(supplierData);
      setFilteredSuppliers(supplierData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar los datos. Verifica que el archivo CSV esté disponible.');
      setLoading(false);
    }
  };

  const loadCustomColumns = () => {
    const saved = localStorage.getItem('supplyMaster_customColumns');
    if (saved) {
      try {
        setCustomColumns(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading custom columns:', e);
      }
    }
  };

  const saveCustomColumns = (columns: CustomColumn[]) => {
    localStorage.setItem('supplyMaster_customColumns', JSON.stringify(columns));
    setCustomColumns(columns);
  };

  const applyFilters = () => {
    let filtered = [...suppliers];

    // Filtrar por cada campo
    if (filters.razonSocial) {
      const term = filters.razonSocial.toLowerCase();
      filtered = filtered.filter(s => s.razonSocial.toLowerCase().includes(term));
    }

    if (filters.articulo) {
      const term = filters.articulo.toLowerCase();
      filtered = filtered.filter(s => s.articulo.toLowerCase().includes(term));
    }

    if (filters.ordenes) {
      const num = parseInt(filters.ordenes);
      if (!isNaN(num)) {
        filtered = filtered.filter(s => s.ordenes >= num);
      }
    }

    if (filters.montoTotalUSD) {
      const num = parseFloat(filters.montoTotalUSD);
      if (!isNaN(num)) {
        filtered = filtered.filter(s => s.montoTotalUSD >= num);
      }
    }

    if (filters.nombreRepresentante) {
      const term = filters.nombreRepresentante.toLowerCase();
      filtered = filtered.filter(s => s.nombreRepresentante.toLowerCase().includes(term));
    }

    if (filters.apellidoRepresentante) {
      const term = filters.apellidoRepresentante.toLowerCase();
      filtered = filtered.filter(s => s.apellidoRepresentante.toLowerCase().includes(term));
    }

    if (filters.telefonoRepresentante) {
      const term = filters.telefonoRepresentante.toLowerCase();
      filtered = filtered.filter(s => s.telefonoRepresentante.toLowerCase().includes(term));
    }

    if (filters.emailRepresentante) {
      const term = filters.emailRepresentante.toLowerCase();
      filtered = filtered.filter(s => s.emailRepresentante.toLowerCase().includes(term));
    }

    if (filters.cuentasContables) {
      const term = filters.cuentasContables.toLowerCase();
      filtered = filtered.filter(s => 
        s.cuentasContables.some(acc => acc.toLowerCase().includes(term))
      );
    }

    if (filters.clases) {
      const term = filters.clases.toLowerCase();
      filtered = filtered.filter(s => 
        s.clases.some(cls => cls.toLowerCase().includes(term))
      );
    }

    setFilteredSuppliers(filtered);
    setPage(0);
  };

  const handleFilterChange = (field: keyof ColumnFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      razonSocial: '',
      articulo: '',
      ordenes: '',
      montoTotalUSD: '',
      nombreRepresentante: '',
      apellidoRepresentante: '',
      telefonoRepresentante: '',
      emailRepresentante: '',
      cuentasContables: '',
      clases: '',
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditSupplier = (supplier: SupplierMaster) => {
    setCurrentSupplier(supplier);
    setEditDialogOpen(true);
  };

  const handleSaveSupplier = () => {
    if (!currentSupplier) return;

    // Actualizar el proveedor en la lista
    const updatedSuppliers = suppliers.map(s => 
      s.id === currentSupplier.id ? { ...currentSupplier, updatedAt: new Date() } : s
    );
    setSuppliers(updatedSuppliers);
    setEditDialogOpen(false);
    setCurrentSupplier(null);
  };

  const handleAddCustomColumn = () => {
    if (customColumns.length >= 5) {
      alert('Máximo 5 columnas customizables permitidas');
      return;
    }

    const newColumn: CustomColumn = {
      id: `custom-${Date.now()}`,
      name: `Columna ${customColumns.length + 1}`,
      type: 'text',
    };

    const updated = [...customColumns, newColumn];
    saveCustomColumns(updated);
  };

  const handleRemoveCustomColumn = (columnId: string) => {
    const updated = customColumns.filter(c => c.id !== columnId);
    saveCustomColumns(updated);
  };

  // Handlers de selección
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = new Set(filteredSuppliers.map(s => s.id));
      setSelected(newSelected);
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.has(id);

  const formatCurrency = (amount: number) => {
    // Formato argentino: punto para miles, sin decimales
    return '$' + Math.round(amount).toLocaleString('es-AR');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Supply Master
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" gutterBottom>
            Supply Master
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {suppliers.length} proveedores registrados
            {hasActiveFilters && ` • ${filteredSuppliers.length} resultados filtrados`}
          </Typography>
        </div>
        <Box display="flex" gap={1}>
          <Tooltip title="Gestionar columnas customizables">
            <IconButton
              color="primary"
              onClick={(e) => setCustomColumnsMenuAnchor(e.currentTarget)}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={clearFilters}
            >
              Limpiar Filtros
            </Button>
          )}
        </Box>
      </Box>

      {/* Menu de columnas customizables */}
      <Menu
        anchorEl={customColumnsMenuAnchor}
        open={Boolean(customColumnsMenuAnchor)}
        onClose={() => setCustomColumnsMenuAnchor(null)}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary">
            Columnas customizables ({customColumns.length}/5)
          </Typography>
        </MenuItem>
        {customColumns.map(col => (
          <MenuItem key={col.id}>
            <Box display="flex" justifyContent="space-between" width="100%">
              <span>{col.name}</span>
              <IconButton
                size="small"
                onClick={() => handleRemoveCustomColumn(col.id)}
              >
                ×
              </IconButton>
            </Box>
          </MenuItem>
        ))}
        {customColumns.length < 5 && (
          <MenuItem onClick={handleAddCustomColumn}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            Agregar columna
          </MenuItem>
        )}
      </Menu>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            {/* Headers principales */}
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.size > 0 && selected.size < filteredSuppliers.length}
                  checked={filteredSuppliers.length > 0 && selected.size === filteredSuppliers.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Razón Social</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Artículo Principal</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 80 }}>Órdenes</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 120 }}>Monto Total USD</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Apellido</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Teléfono</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>Cuentas Contables</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>Clases</TableCell>
              {customColumns.map(col => (
                <TableCell key={col.id} sx={{ fontWeight: 'bold', minWidth: 120 }}>
                  {col.name}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Acciones</TableCell>
            </TableRow>
            
            {/* Fila de filtros */}
            <TableRow>
              <TableCell padding="checkbox">
                {/* Vacío - sin filtro para checkbox */}
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.razonSocial}
                  onChange={(e) => handleFilterChange('razonSocial', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.articulo}
                  onChange={(e) => handleFilterChange('articulo', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Min..."
                  value={filters.ordenes}
                  onChange={(e) => handleFilterChange('ordenes', e.target.value)}
                  fullWidth
                  variant="standard"
                  type="number"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Min..."
                  value={filters.montoTotalUSD}
                  onChange={(e) => handleFilterChange('montoTotalUSD', e.target.value)}
                  fullWidth
                  variant="standard"
                  type="number"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.nombreRepresentante}
                  onChange={(e) => handleFilterChange('nombreRepresentante', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.apellidoRepresentante}
                  onChange={(e) => handleFilterChange('apellidoRepresentante', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.telefonoRepresentante}
                  onChange={(e) => handleFilterChange('telefonoRepresentante', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.emailRepresentante}
                  onChange={(e) => handleFilterChange('emailRepresentante', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.cuentasContables}
                  onChange={(e) => handleFilterChange('cuentasContables', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filtrar..."
                  value={filters.clases}
                  onChange={(e) => handleFilterChange('clases', e.target.value)}
                  fullWidth
                  variant="standard"
                />
              </TableCell>
              {customColumns.map(col => (
                <TableCell key={col.id}></TableCell>
              ))}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {filteredSuppliers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((supplier) => {
                const isItemSelected = isSelected(supplier.id);
                
                return (
                  <TableRow 
                    key={supplier.id} 
                    hover
                    onClick={() => handleSelectOne(supplier.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>{supplier.razonSocial}</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>{supplier.articulo}</TableCell>
                    <TableCell align="right" sx={{ minWidth: 80 }}>{supplier.ordenes.toLocaleString('es-AR')}</TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>{formatCurrency(supplier.montoTotalUSD)}</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>{supplier.nombreRepresentante}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>{supplier.apellidoRepresentante}</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>{supplier.telefonoRepresentante}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      {supplier.emailRepresentante}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {supplier.cuentasContables.slice(0, 2).map((acc, idx) => (
                          <Chip
                            key={idx}
                            label={acc}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {supplier.cuentasContables.length > 2 && (
                          <Chip
                            label={`+${supplier.cuentasContables.length - 2}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {supplier.clases.slice(0, 2).map((cls, idx) => (
                          <Chip
                            key={idx}
                            label={cls}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                        {supplier.clases.length > 2 && (
                          <Chip
                            label={`+${supplier.clases.length - 2}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    {customColumns.map(col => (
                      <TableCell key={col.id}>
                        {supplier.customFields?.[col.id] || '-'}
                      </TableCell>
                    ))}
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSupplier(supplier);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredSuppliers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
        />
      </TableContainer>

      {/* Dialog de edición */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Proveedor</DialogTitle>
        <DialogContent>
          {currentSupplier && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Razón Social"
                  value={currentSupplier.razonSocial}
                  onChange={(e) => setCurrentSupplier({
                    ...currentSupplier,
                    razonSocial: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Artículo Principal"
                  value={currentSupplier.articulo}
                  onChange={(e) => setCurrentSupplier({
                    ...currentSupplier,
                    articulo: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Nombre Representante"
                  value={currentSupplier.nombreRepresentante}
                  onChange={(e) => setCurrentSupplier({
                    ...currentSupplier,
                    nombreRepresentante: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Apellido Representante"
                  value={currentSupplier.apellidoRepresentante}
                  onChange={(e) => setCurrentSupplier({
                    ...currentSupplier,
                    apellidoRepresentante: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Teléfono Representante"
                  value={currentSupplier.telefonoRepresentante}
                  onChange={(e) => setCurrentSupplier({
                    ...currentSupplier,
                    telefonoRepresentante: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Email Representante"
                  value={currentSupplier.emailRepresentante}
                  onChange={(e) => setCurrentSupplier({
                    ...currentSupplier,
                    emailRepresentante: e.target.value
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Nota: Órdenes, Monto Total, Cuentas Contables y Clases se calculan automáticamente desde el historial
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSaveSupplier} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplyMaster;
